
import { Component, Prop, Vue } from "vue-property-decorator";
import { ciTrackingService } from "@services/ci-tracking.service";
import { gtmTrackingService } from "@services/gtm-tracking.service";
import { apiService } from "@services/api.service";
import { CreateToast } from "@utils/toasts";
import { TranslateResult } from "vue-i18n";
import { ROUTENAMES } from "@models/constants/routes";
import { Constants } from "@app/constants";

@Component({
  name: "ci-registration-button",
})
export default class extends Vue {
  @Prop({ default: "" }) public wrapperClasses!: string;
  @Prop({ default: "" }) public innerClasses!: string;
  @Prop({ default: "EventTile" }) public trackingContext!: string;
  @Prop({ default: false }) public participationCallFailed!: boolean;
  @Prop({}) public event!: Events.Event;

  public mouseOver = false;

  get eventDate(): Date {
    return this.event.event_date ? new Date(this.event.event_date) : new Date();
  }

  get eventEndDate(): Date {
    return this.event.event_end_date ? new Date(this.event.event_end_date) : new Date();
  }

  get eventIsRunning(): boolean {
    return this.eventDate <= new Date() && this.eventEndDate > new Date();
  }

  get isParticipating(): boolean {
    return this.event.participationState === "participating";
  }

  get buttonClass(): string {
    let classString;
    if (this.event.participationState === "loading") {
      classString = "loading";
    } else if (this.eventIsRunning) {
      classString = "running";
    } else if (this.eventDate <= new Date()) {
      classString = "past";
    } else if (this.participationCallFailed) {
      classString = "registration.not_possible";
    } else if (this.isParticipating) {
      classString = "registered";
    } else {
      classString = "register";
    }
    return classString;
  }

  get buttonLabel(): TranslateResult | string {
    let labelKey = this.buttonClass;
    const isRunning = this.eventIsRunning;
    const participating = this.isParticipating;

    if (this.mouseOver && participating) {
      if (isRunning) {
        labelKey = "running";
      } else {
        labelKey = "unregister";
      }
    }

    return this.$t(`ui.components.social_events.tile.${labelKey}`);
  }

  get buttonVariant(): string {
    if (this.isParticipating) return "outline-danger";

    return "filled-primary";
  }

  get iconClass(): string {
    if (this.event.participationState === "loading") return "mdi-loading mdi-spin";

    return "";
  }

  public handleMouseOver(): void {
    this.mouseOver = true;
  }

  public handleMouseLeave(): void {
    this.mouseOver = false;
  }

  public toggleRegistration(): void {
    if (this.eventIsRunning) {
      window.location.href = this.event.event_url;
    } else if (this.isParticipating) {
      this.withdraw(this.trackingContext);
    } else {
      this.signUp(this.trackingContext);
    }
  }

  private signUp(context: string): void {
    if (!this.event) return;

    const allowedToRegister = !this.event.professional_only || this.$user.professional;

    ciTrackingService.trackClick(
      "ButtonRegister",
      this.event.id.toString(),
      context,
      null,
      allowedToRegister ? Constants.ciTracking.eventValueAccessible : Constants.ciTracking.eventValueGated
    );

    if (allowedToRegister) {
      apiService.social_events
        .register(this.event.id)
        .then((response) => {
          this.event.participationState = response.data.registered ? "participating" : "not_participating";
          CreateToast.success(
            this.$t("ui.components.social_events.tile.registration.success.message", {
              event: this.event.headline,
            }) as string,
            { title: this.$t("ui.components.social_events.tile.registration.success.title") as string }
          );
          gtmTrackingService.track({
            eventCategory: "Event",
            eventAction: "Registration",
            eventLabel: { title: this.event.headline },
          });
        })
        .catch(() => CreateToast.error(this.$t("ui.components.social_events.tile.registration.failed") as string));
    } else {
      CreateToast.warning(this.$t("ui.shared.global.toasts.professional_only_html") as string, {
        to: { name: ROUTENAMES.USERS_SETTINGS, query: { tab: "investment_profile" } },
      });
    }
  }

  private withdraw(context: string): void {
    if (!this.event) return;

    ciTrackingService.trackClick("ButtonRegister", this.event.id.toString(), context);

    apiService.social_events
      .unregister(this.event.id)
      .then((response) => {
        this.event.participationState = response.data.registered ? "participating" : "not_participating";
        CreateToast.warning(
          this.$t("ui.components.social_events.tile.unregistration.success.message", {
            event: this.event.headline,
          }) as string,
          { title: this.$t("ui.components.social_events.tile.unregistration.success.title") as string }
        );
      })
      .catch(() => CreateToast.error(this.$t("ui.components.social_events.tile.unregistration.failed") as string));
  }
}
